import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, Upload, X } from "@phosphor-icons/react";
import useSeo from "../hooks/useSeo";

export default function Pricing() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [packages, setPackages] = useState([]);
    const [selected, setSelected] = useState(null);
    const [txnRef, setTxnRef] = useState("");
    const [screenshot, setScreenshot] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [couponCode, setCouponCode] = useState("");
    const [couponResult, setCouponResult] = useState(null);
    const [couponChecking, setCouponChecking] = useState(false);
    // Mobile slider state
    const sliderRef = useRef(null);
    const cardRefs = useRef([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const userInteractingRef = useRef(false);
    const userInteractTimeoutRef = useRef(null);

    // Track the most-visible card while scrolling on mobile.
    useEffect(() => {
        const root = sliderRef.current;
        if (!root || packages.length === 0) return;
        const observer = new IntersectionObserver(
            (entries) => {
                let best = null;
                entries.forEach((e) => {
                    if (!best || e.intersectionRatio > best.intersectionRatio) {
                        best = e;
                    }
                });
                if (best && best.isIntersecting) {
                    const idx = Number(
                        best.target.getAttribute("data-slide-idx"),
                    );
                    if (!Number.isNaN(idx)) setActiveIdx(idx);
                }
            },
            { root, threshold: [0.5, 0.75, 1] },
        );
        cardRefs.current.forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, [packages.length]);

    // Auto-advance the mobile slider every 4s; pause on user interaction.
    useEffect(() => {
        if (packages.length <= 1) return;
        const id = setInterval(() => {
            if (userInteractingRef.current) return;
            const root = sliderRef.current;
            if (!root) return;
            // Skip auto-advance if slider isn't on screen or grid layout is active.
            if (window.matchMedia("(min-width: 768px)").matches) return;
            const next = (activeIdx + 1) % packages.length;
            const target = cardRefs.current[next];
            if (target) {
                target.scrollIntoView({
                    behavior: "smooth",
                    inline: "center",
                    block: "nearest",
                });
            }
        }, 4000);
        return () => clearInterval(id);
    }, [activeIdx, packages.length]);

    const markUserInteraction = () => {
        userInteractingRef.current = true;
        if (userInteractTimeoutRef.current) {
            clearTimeout(userInteractTimeoutRef.current);
        }
        userInteractTimeoutRef.current = setTimeout(() => {
            userInteractingRef.current = false;
        }, 6000);
    };

    const goToSlide = (i) => {
        markUserInteraction();
        const target = cardRefs.current[i];
        if (target) {
            target.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    };

    useSeo({ path: "/pricing" });

    useEffect(() => {
        api.get("/packages")
            .then(({ data }) => {
                // Order: Trial first, then by ascending price (Starter, Standard, Premium)
                const sorted = [...data].sort((a, b) => {
                    if (a.is_trial && !b.is_trial) return -1;
                    if (!a.is_trial && b.is_trial) return 1;
                    return Number(a.price) - Number(b.price);
                });
                setPackages(sorted);
            })
            .catch(() => {});
    }, []);

    const readFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => setScreenshot(reader.result);
        reader.readAsDataURL(file);
    };

    const submitPayment = async () => {
        if (!user) return nav("/register");
        if (!selected) return toast.error("Pick a package");
        if (selected.is_trial) {
            return activateTrial(selected);
        }
        // A typed but un-applied code: apply it first so the buyer sees (and
        // pays) the discounted total, instead of silently dropping it.
        if (couponCode.trim() && !couponResult) {
            const ok = await applyCoupon();
            if (ok) toast.info("Coupon applied — please pay the new total, then submit again.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/payments", {
                package_id: selected.id,
                coupon_code: couponResult ? couponCode.trim().toUpperCase() : undefined,
                transaction_ref: txnRef,
                screenshot_base64: screenshot,
            });
            toast.success("Payment submitted. Admin will verify shortly.");
            setSelected(null);
            setTxnRef("");
            setScreenshot("");
            setCouponCode("");
            setCouponResult(null);
            nav("/dashboard");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) || err.message,
            );
        } finally {
            setSubmitting(false);
        }
    };

    const applyCoupon = async (codeOverride) => {
        const code = String(codeOverride ?? couponCode).trim();
        if (!selected || !code) return false;
        setCouponChecking(true);
        try {
            const { data } = await api.post("/coupons/validate", {
                code,
                package_id: selected.id,
            });
            setCouponResult(data);
            toast.success(`Coupon applied — you save ₹${Number(data.discount_amount).toFixed(0)}`);
            return true;
        } catch (err) {
            setCouponResult(null);
            toast.error(err.response?.data?.error || "Invalid or expired coupon");
            return false;
        } finally {
            setCouponChecking(false);
        }
    };

    // Hand-off from the site-wide OfferPopup ("Claim this offer") — pre-fill
    // and auto-validate the promoted code as soon as a package is picked.
    useEffect(() => {
        if (!selected) return;
        const pending = sessionStorage.getItem("pending_coupon_code");
        if (!pending) return;
        sessionStorage.removeItem("pending_coupon_code");
        setCouponCode(pending);
        applyCoupon(pending);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected]);

    const activateTrial = async (pkg) => {
        if (!user) return nav("/register");
        setSubmitting(true);
        try {
            await api.post(`/packages/${pkg.id}/activate-trial`);
            await refresh();
            toast.success(
                "Trial activated. You can now design and export cards.",
            );
            setSelected(null);
            nav("/dashboard");
        } catch (err) {
            toast.error(
                formatApiErrorDetail(err.response?.data?.detail) ||
                    err.response?.data?.error ||
                    err.message,
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="max-w-7xl mx-auto px-6 py-16"
            data-testid="pricing-page"
        >
            <div className="mb-12">
                <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest mb-3">
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-pink-600 font-bold">
                        ◆ Packages
                    </span>
                </div>
                <h1 className="font-display font-black text-5xl tracking-tighter">
                    Pay as your institution{" "}
                    <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 via-blue-600 to-pink-600">
                        grows.
                    </span>
                </h1>
                <p className="text-zinc-600 mt-3 max-w-2xl">
                    Pick a package. Transfer payment via UPI / bank transfer.
                    Upload the screenshot — our admin verifies within hours and
                    credits unlock automatically.
                </p>
            </div>

            <div
                ref={sliderRef}
                onTouchStart={markUserInteraction}
                onPointerDown={markUserInteraction}
                onWheel={markUserInteraction}
                className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none no-scrollbar scroll-smooth -mx-6 px-6 md:mx-0 md:px-0 pt-5 md:pt-0 pb-2 md:pb-0"
            >
                {packages.map((p, idx) => {
                    const trial = Boolean(p.is_trial);
                    // Highlight "Standard" as Most Popular (the package named Standard,
                    // falling back to the second non-trial card).
                    const nonTrialIndex = packages
                        .filter((x) => !x.is_trial)
                        .findIndex((x) => x.id === p.id);
                    const featured =
                        !trial &&
                        (String(p.name).toLowerCase() === "standard" ||
                            (packages.every(
                                (x) =>
                                    String(x.name).toLowerCase() !== "standard",
                            ) &&
                                nonTrialIndex === 1));
                    const isActiveSlide = idx === activeIdx;
                    return (
                        <div
                            key={p.id}
                            ref={(el) => (cardRefs.current[idx] = el)}
                            data-slide-idx={idx}
                            className={`p-7 relative flex flex-col border transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md snap-center md:snap-align-none shrink-0 w-[85%] sm:w-[60%] md:w-auto ${featured ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-950 border-zinc-200"} ${isActiveSlide ? "scale-[1.02] shadow-xl md:scale-100 md:shadow-none" : "scale-95 opacity-80 md:scale-100 md:opacity-100"} ${featured && isActiveSlide ? "shadow-glow-pink" : ""}`}
                            data-testid={`package-card-${p.id}`}
                        >
                            {featured && (
                                <div className="absolute -top-3 left-6 g-pink text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 shadow-glow-pink">
                                    Most Popular
                                </div>
                            )}
                            {trial && (
                                <div className="absolute -top-3 right-6 bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1">
                                    Free Trial
                                </div>
                            )}
                            <div className="font-display font-black text-2xl tracking-tight">
                                {p.name}
                            </div>
                            <div
                                className={`text-sm mt-1 ${featured ? "text-zinc-400" : "text-zinc-500"}`}
                            >
                                {p.description}
                            </div>
                            {Number(p.effective_price) < Number(p.price) ? (
                                <div className="mt-6 flex items-baseline gap-2 flex-wrap">
                                    <span
                                        className={`font-display font-black text-5xl tracking-tighter ${featured ? "text-emerald-400" : "text-emerald-600"}`}
                                    >
                                        ₹{Number(p.effective_price).toFixed(0)}
                                    </span>
                                    <span
                                        className={`line-through text-lg ${featured ? "text-zinc-500" : "text-zinc-400"}`}
                                    >
                                        ₹{Number(p.price).toFixed(0)}
                                    </span>
                                </div>
                            ) : (
                                <div className="mt-6 font-display font-black text-5xl tracking-tighter">
                                    ₹{Number(p.price).toFixed(0)}
                                </div>
                            )}
                            <div
                                className={`text-xs font-mono uppercase tracking-widest ${featured ? "text-zinc-400" : "text-zinc-500"}`}
                            >
                                One-time
                                {Number(p.effective_price) < Number(p.price) &&
                                    p.discount_valid_until && (
                                        <>
                                            {" "}
                                            · Offer ends{" "}
                                            {new Date(
                                                p.discount_valid_until,
                                            ).toLocaleDateString()}
                                        </>
                                    )}
                            </div>
                            <ul className="mt-6 space-y-2 text-sm flex-1">
                                <li className="flex items-center gap-2">
                                    <CheckCircle
                                        size={16}
                                        color="#16A34A"
                                        weight="fill"
                                    />{" "}
                                    {Number(p.card_limit).toLocaleString()} card
                                    credits
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle
                                        size={16}
                                        color="#16A34A"
                                        weight="fill"
                                    />{" "}
                                    Unlimited templates
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle
                                        size={16}
                                        color="#16A34A"
                                        weight="fill"
                                    />{" "}
                                    Bulk Excel import
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle
                                        size={16}
                                        color="#16A34A"
                                        weight="fill"
                                    />{" "}
                                    PDF, PNG, print export
                                </li>
                                {trial && (
                                    <li className="flex items-center gap-2 text-amber-600">
                                        <CheckCircle
                                            size={16}
                                            color="#D97706"
                                            weight="fill"
                                        />{" "}
                                        Exports include watermark
                                    </li>
                                )}
                            </ul>
                            <button
                                onClick={() => {
                                    setSelected(p);
                                    setCouponCode("");
                                    setCouponResult(null);
                                }}
                                className={`mt-6 w-full ${featured ? "bg-white text-zinc-950 hover:bg-zinc-100" : "bg-zinc-950 text-white hover:bg-zinc-800"} py-3 text-sm font-medium transition-colors`}
                                data-testid={`select-package-btn-${p.id}`}
                            >
                                {trial
                                    ? "Start free trial"
                                    : `Select ${p.name}`}
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Mobile dots indicator */}
            {packages.length > 1 && (
                <div
                    className="flex md:hidden items-center justify-center gap-2 mt-5"
                    aria-label="Package slider pagination"
                >
                    {packages.map((p, i) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => goToSlide(i)}
                            aria-label={`Go to ${p.name}`}
                            className={`h-2 rounded-full transition-all duration-300 ${
                                i === activeIdx
                                    ? "w-8 bg-gradient-to-r from-violet-600 via-blue-600 to-pink-600"
                                    : "w-2 bg-zinc-300 hover:bg-zinc-400"
                            }`}
                            data-testid={`pricing-slider-dot-${i}`}
                        />
                    ))}
                </div>
            )}

            {/* Payment upload modal */}
            {selected && (
                <div
                    className="fixed inset-0 z-50 bg-zinc-950/60 flex items-center justify-center p-4"
                    data-testid="payment-modal"
                >
                    <div className="bg-white w-full max-w-lg border border-zinc-300">
                        <div className="flex items-center justify-between p-5 border-b border-zinc-200">
                            <div>
                                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
                                    Submit payment
                                </div>
                                <div className="font-display font-bold text-xl">
                                    {selected.name} — ₹
                                    {Number(
                                        couponResult
                                            ? couponResult.final_amount
                                            : (selected.effective_price ?? selected.price ?? 0),
                                    ).toFixed(0)}
                                </div>
                            </div>
                            <button
                                className="btn-ghost"
                                onClick={() => {
                                    setSelected(null);
                                    setCouponCode("");
                                    setCouponResult(null);
                                }}
                                data-testid="payment-close-btn"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className="p-5 space-y-4">
                            {selected.is_trial && (
                                <div className="bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
                                    Trial package is auto-approved once
                                    activated. Watermark will be applied on
                                    exports.
                                </div>
                            )}
                            {!selected.is_trial && (
                                <div className="bg-zinc-100 p-4 text-sm space-y-3 font-mono">
                                    <div className="flex flex-col sm:flex-row gap-4 items-center">
                                        {(() => {
                                            const upiId = "idcardgen@ybl";
                                            const amount = Number(
                                                couponResult
                                                    ? couponResult.final_amount
                                                    : (selected.effective_price ??
                                                          selected.price ??
                                                          0),
                                            ).toFixed(2);
                                            const note = encodeURIComponent(
                                                `IDCardGen ${selected.name}`,
                                            );
                                            const upiLink = `upi://pay?pa=${upiId}&pn=IDCardGen&am=${amount}&cu=INR&tn=${note}`;
                                            const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=1&data=${encodeURIComponent(upiLink)}`;
                                            return (
                                                <>
                                                    <a
                                                        href={upiLink}
                                                        className="shrink-0 bg-white p-2 border border-zinc-300"
                                                        title="Tap on mobile to pay"
                                                    >
                                                        <img
                                                            src={qrUrl}
                                                            alt={`UPI QR ₹${amount}`}
                                                            width={160}
                                                            height={160}
                                                            className="block"
                                                        />
                                                    </a>
                                                    <div className="space-y-1 text-center sm:text-left">
                                                        <div>
                                                            UPI ID:{" "}
                                                            <span className="font-bold">
                                                                {upiId}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            Amount:{" "}
                                                            <span className="font-bold">
                                                                ₹{amount}
                                                            </span>
                                                        </div>
                                                        <div className="text-xs text-zinc-500">
                                                            Scan with any UPI
                                                            app (GPay, PhonePe,
                                                            Paytm, BHIM)
                                                        </div>
                                                        <a
                                                            href={upiLink}
                                                            className="inline-block mt-1 text-xs font-semibold text-blue-700 underline sm:hidden"
                                                        >
                                                            Open in UPI app →
                                                        </a>
                                                    </div>
                                                </>
                                            );
                                        })()}
                                    </div>
                                </div>
                            )}
                            {!selected.is_trial && (
                                <div>
                                    <label className="label-swiss">
                                        Coupon code (optional)
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            className="input-swiss flex-1 uppercase"
                                            value={couponCode}
                                            onChange={(e) => {
                                                setCouponCode(e.target.value);
                                                setCouponResult(null);
                                            }}
                                            placeholder="e.g. SAVE20"
                                            data-testid="coupon-code-input"
                                        />
                                        <button
                                            type="button"
                                            className="btn-secondary shrink-0"
                                            disabled={couponChecking || !couponCode.trim()}
                                            onClick={() => applyCoupon()}
                                            data-testid="coupon-apply-btn"
                                        >
                                            {couponChecking ? "Checking…" : "Apply"}
                                        </button>
                                    </div>
                                    {couponResult && (
                                        <div className="mt-2 text-sm text-emerald-700">
                                            You save ₹
                                            {Number(couponResult.discount_amount).toFixed(0)} —
                                            Total: ₹
                                            {Number(couponResult.final_amount).toFixed(0)}
                                        </div>
                                    )}
                                </div>
                            )}
                            {!selected.is_trial && (
                                <div>
                                    <label className="label-swiss">
                                        Transaction reference / UTR
                                    </label>
                                    <input
                                        className="input-swiss"
                                        value={txnRef}
                                        onChange={(e) =>
                                            setTxnRef(e.target.value)
                                        }
                                        placeholder="UPI ref / UTR number"
                                        data-testid="payment-txn-input"
                                    />
                                </div>
                            )}
                            {!selected.is_trial && (
                                <div>
                                    <label className="label-swiss">
                                        Payment screenshot
                                    </label>
                                    <label className="flex items-center justify-center gap-2 border border-dashed border-zinc-400 p-6 text-sm cursor-pointer hover:border-blue-600">
                                        <Upload size={18} />
                                        <span>
                                            {screenshot
                                                ? "Change screenshot"
                                                : "Upload PNG / JPG"}
                                        </span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={readFile}
                                            data-testid="payment-screenshot-input"
                                        />
                                    </label>
                                    {screenshot && (
                                        <img
                                            src={screenshot}
                                            alt="Payment"
                                            className="mt-3 max-h-40 border border-zinc-200"
                                        />
                                    )}
                                </div>
                            )}
                            <button
                                disabled={submitting}
                                onClick={submitPayment}
                                className="btn-primary w-full"
                                data-testid="payment-submit-btn"
                            >
                                {submitting
                                    ? "Submitting…"
                                    : selected.is_trial
                                      ? "Activate trial"
                                      : "Submit for verification"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
